import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const defaultFormValues = {
  title: "",
  body: "",
  linkText: "",
  linkUrl: "",
  displayOrder: "",
  visible: true,
};

const CreateEditVoteInfoModal = ({
  open,
  handleClose,
  voteInfoData,
  onSave,
  isSaving = false,
}) => {
  const isEdit = Boolean(voteInfoData);
  const [formValues, setFormValues] = useState(defaultFormValues);

  useEffect(() => {
    if (open) {
      setFormValues(voteInfoData || defaultFormValues);
    }
  }, [open, voteInfoData]);

  const bodyCount = useMemo(
    () => formValues.body.length,
    [formValues.body],
  );

  const handleChange = (field) => (event) => {
    const value =
      field === "visible" ? event.target.checked : event.target.value;

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    onSave({
      ...formValues,
      displayOrder:
        formValues.displayOrder === "" ? "" : Number(formValues.displayOrder),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "#081B44",
          color: "#fff",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>
          {isEdit ? "Edit Vote Info Box" : "New Vote Info Box"}
        </Typography>

        <IconButton
          onClick={handleClose}
          disabled={isSaving}
          sx={{
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.35)",
            width: 30,
            height: 30,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={labelStyle}>TITLE *</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Check Your Registration"
              value={formValues.title}
              onChange={handleChange("title")}
            />
          </Box>

          <Box>
            <Typography sx={labelStyle}>BODY TEXT *</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Short description of this resource (max 300 characters)"
              value={formValues.body}
              onChange={handleChange("body")}
              slotProps={{ htmlInput: { maxLength: 300 } }}
            />
            <Typography sx={helperStyle}>{bodyCount} / 300 characters</Typography>
          </Box>

          <Box>
            <Typography sx={labelStyle}>LINK TEXT *</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Check at iVote.de.gov →"
              value={formValues.linkText}
              onChange={handleChange("linkText")}
            />
            <Typography sx={helperStyle}>
              The clickable text shown at the bottom of the box.
            </Typography>
          </Box>

          <Box>
            <Typography sx={labelStyle}>LINK URL *</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="https://..."
              value={formValues.linkUrl}
              onChange={handleChange("linkUrl")}
            />
            <Typography sx={helperStyle}>
              Where the link goes. Must start with http:// or https://
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: "1 1 220px" }}>
              <Typography sx={labelStyle}>DISPLAY ORDER</Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={formValues.displayOrder}
                onChange={handleChange("displayOrder")}
              />
              <Typography sx={helperStyle}>
                Lower numbers appear first. Leave blank to add at end.
              </Typography>
            </Box>

            <Box
              sx={{
                flex: "1 1 240px",
                bgcolor: "#F7F9FC",
                borderRadius: "4px",
                px: 1,
                py: 0.5,
                mt: 2.4,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                  checked={formValues.visible}
                  onChange={handleChange("visible")}
                    sx={{
                      color: "#B0B7C3",
                      "&.Mui-checked": {
                        color: "#1976D2",
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px", color: "#25324B" }}>
                    Visible on public site
                  </Typography>
                }
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 3,
            pt: 2,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={isSaving}
            sx={{
              bgcolor: "#E5E7EB",
              color: "#0F1E3D",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              bgcolor: "#D4A017",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": {
                bgcolor: "#B8860B",
              },
            }}
          >
            {isSaving ? "Saving..." : "Save Box"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const labelStyle = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#25324B",
  letterSpacing: "0.4px",
  mb: 0.75,
};

const helperStyle = {
  fontSize: "11px",
  color: "#98A2B3",
  mt: 0.75,
};

export default CreateEditVoteInfoModal;
